<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import WebGLZoneAnimation from '@/components/webgl/WebGLZoneAnimation.vue';
import { useAnimatedElements, useMap, useWebGLContexts } from '@/composables';
import type { ZoneAnimation } from '@/types';

const { animations } = useAnimatedElements();
const { isTransformationBeingApplied } = useMap();

const zoneEls = ref<HTMLDivElement[]>([]);
const restoreState = ref<boolean>(false); // recalculate sprite position after zoom transition

const excludedAnimations = ref<number[]>([]);
const filteredAnimations = ref<FilteredAnimation[]>([]);

export type FilteredAnimation = {
  animations: ZoneAnimation;
  showWebGL: boolean;
};

const calculateAnimations = () => {
  filteredAnimations.value = animations.value
    .filter((zone) => {
      const index = parseInt(zone[0].name.match(/\d+/)![0]);
      const engine = zone[0].engine;
      return engine == 'webgl' && !excludedAnimations.value.includes(index);
    })
    .map((zone, index) => ({
      animations: zone,
      showWebGL: index < numActive.value,
    }));
};

const { numActive, getMaxWebGLContexts } = useWebGLContexts();

onMounted(async () => {
  await getMaxWebGLContexts();
});

useIntersectionObserver(
  zoneEls,
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.target.children[0]) {
        return;
      }

      if (entry.isIntersecting) {
        entry.target.children[0].classList.remove('hidden');
      } else {
        entry.target.children[0].classList.add('hidden');
      }
    });
  },
  {
    immediate: true,
    threshold: 0,
  },
);

watch(isTransformationBeingApplied, (newValue) => {
  if (newValue) {
    restoreState.value = true;
  }
});

watch(numActive, (newValue) => {
  if (newValue) {
    calculateAnimations();
  }
});
</script>

<template>
  <template v-for="(zone, index) in filteredAnimations" :key="index">
    <div
      :style="{
        transform: `translate3d(${zone.animations[0].left}px, ${zone.animations[0].top}px, 0)`,
        width: `${zone.animations[0].width}px`,
        height: `${zone.animations[0].height}px`,
      }"
      ref="zoneEls"
      class="pointer-events-none absolute z-rgb-animations origin-top-left select-none overflow-hidden"
      :id="zone.animations[0].name"
    >
      <WebGLZoneAnimation :animations="zone.animations" :show="zone.showWebGL" />
    </div>
  </template>
</template>
